import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import $api from "@/zapi-frontend/shared/api/axios";
import { RotateCw } from "lucide-react";
import { PlusIcon } from "@phosphor-icons/react";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { ConfirmDialog } from "@/zapi-frontend/shared/ui/ConfirmDialog/ConfirmDialog";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getRouteCreateProject } from "@/zapi-frontend/shared/const/router";
import { useGetProjectsList, GET_PROJECTS_LIST_KEY } from "@/zapi-frontend/shared/services/useGetProjectsList";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useProjectDimensions } from "@/zapi-frontend/shared/services/useProjectDimensions";
import { updateCustomSnippetsUrl } from "@/zapi-frontend/pages/SettingsPage/services/updateCustomSnippetsUrl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { defineIsInvalidUrlError } from "../lib/defineIsInvalidUrlError";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { ProjectsCards } from "./ProjectsCards";
import { HEADER_CENTER_SLOT_ID } from "@/zapi-frontend/widgets/Header";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { LatticeField } from "@libs/zapi-ui/ds/components/lattice-field";
import { PageLoader } from "@/zapi-frontend/shared/ui/PageLoader";
import {
  AppShellAside,
  AppShellCenter,
  AppShellContent,
  AppShellMain,
  AppShellPanels
} from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ProjectLayout/ui/blocks/AppShell";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { DuplicateProjectModal } from "@/zapi-frontend/widgets/Header/ui/DuplicateProjectModal";
import { sortKeyToField, type TSystemColumn } from "../lib/portfolioStorage";
import { usePortfolioDerivedList } from "../lib/usePortfolioDerivedList";
import { buildPortfolioChatScope } from "../lib/portfolioChatFilterScope";
import { usePortfolioPrefs, filterKeyFromUrl } from "../lib/usePortfolioPrefs";
import { ChatPanel } from "./ProjectListV2/_ChatPanel";
import { LeoLauncher } from "@/zapi-frontend/shared/ui/ResponsiveChatPanel";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";
import {
  countVisibleAssistantMessages,
  useChatUnreadIndicator
} from "@/zapi-frontend/shared/hooks/useChatUnreadIndicator";
import { useProjectsListChatStore } from "@/zapi-frontend/stores/projectsListChatStore";
import { EmptyOrgState } from "./ProjectListV2/_EmptyOrgState";
import { NoProjectsFoundState } from "./ProjectListV2/_NoProjectsFoundState";
import { PortfolioInvalidUrlState } from "./_PortfolioInvalidUrlState";
import { isFiltersEmpty } from "../lib/matchSavedFilter";
import { CommitHash } from "@/zapi-frontend/shared/ui/custom/CommitHash";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { useSavedFilterActions, useSaveCurrentFilters } from "./SavedFiltersControl/useSavedFilterActions";
import { FilterSearch } from "./ProjectListRedesign/_FilterSearch";
import { FiltersPanel } from "./ProjectListRedesign/_FiltersPanel";

export function ProjectsPage() {
  const { t } = useTranslation(["projects", "common"]);
  const { setProjects, projects, archiveProject, duplicateProject } = useProjectStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search") ?? "";
  const { setMe, role, accessMode, isLabs } = useMeStore();
  const currentOrganizationId = useOrganizationStore((s) => s.currentOrganizationId);
  const hasEdit = hasEditAccess(role, accessMode);
  const isChatDocked = useMediaQuery("(min-width: 1168px)");
  const [chatOpen, setChatOpen] = useState(false);
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);
  useEffect(() => setHeaderSlot(document.getElementById(HEADER_CENTER_SLOT_ID)), []);
  // Below lg the toolbar wraps under the brand, so the header is taller than --app-header-height.
  const [headerHeight, setHeaderHeight] = useState<number | null>(null);
  useEffect(() => {
    let observer: ResizeObserver | undefined;
    let frame = 0;
    const attach = () => {
      const header = document.querySelector<HTMLElement>('[data-slot="app-header"]');
      if (!header) {
        frame = requestAnimationFrame(attach);
        return;
      }
      setHeaderHeight(header.offsetHeight);
      observer = new ResizeObserver(() => setHeaderHeight(header.offsetHeight));
      observer.observe(header);
    };
    attach();
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, []);
  const assistantMessageCount = useProjectsListChatStore((s) =>
    countVisibleAssistantMessages(currentOrganizationId ? s.historyByOrg[currentOrganizationId] ?? [] : [])
  );
  const { hasUnread: hasUnreadChat, markSeen: markChatSeen } = useChatUnreadIndicator(assistantMessageCount, {
    ready: currentOrganizationId !== null,
    firstVisitKey: "projects-list",
    conversationKey: String(currentOrganizationId)
  });

  const [chatDockHidden, setChatDockHidden] = useState(false);
  const toggleChat = (open: boolean) => {
    setChatOpen(open);
    setChatDockHidden(!open);
    markChatSeen();
  };

  const canDuplicateProject = !!isLabs;

  const queryClient = useQueryClient();
  const { data, isPending: isLoadingProjects, error, refetch } = useGetProjectsList();
  const { data: dimensions = [], isFetched: dimensionsLoaded } = useProjectDimensions();

  const { prefs, filters, persist, toggleFilterValue, clearAllFilters } = usePortfolioPrefs({
    organizationId: currentOrganizationId,
    dimensions,
    dimensionsLoaded
  });

  const [duplicateSourceId, setDuplicateSourceId] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);

  const sortField = useMemo(() => sortKeyToField(prefs.sortFieldKey, dimensions), [prefs.sortFieldKey, dimensions]);
  const deferredFilters = useDeferredValue(filters);
  const derived = usePortfolioDerivedList(projects, search, deferredFilters, sortField, prefs.sortDir);
  const chatFilterScope = useMemo(
    () => buildPortfolioChatScope(derived, search, deferredFilters, dimensions),
    [derived, search, deferredFilters, dimensions]
  );

  const activeFilterCount = Object.values(filters).reduce((n, s) => n + s.size, 0);
  const hiddenSet = new Set(prefs.hiddenDimensionIds);
  const hiddenSysSet = new Set(prefs.hiddenSystemColumns as TSystemColumn[]);

  const invalidUrlMutation = useMutation({
    mutationFn: updateCustomSnippetsUrl,
    onSuccess: () => {
      setMe({ customSnippetsUrl: null });
      refetch();
    }
  });

  const isLoading = isLoadingProjects || invalidUrlMutation.isPending;
  const isError = Boolean(error);
  const isInvalidUrlError = defineIsInvalidUrlError(error);

  const invalidateProjectsList = () => {
    queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY, currentOrganizationId] });
  };

  const handleDuplicateConfirm = async (orgId: number) => {
    if (!duplicateSourceId) return;
    const [, err] = await tryCatch<void, AxiosError<{ message: string }>>(
      duplicateProject(duplicateSourceId, orgId, { includeAllVersions: true })
    );
    if (err) {
      toast.error(err.response?.data.message || t("errors.duplicateFailed"));
      return;
    }
    toast.success(t("toasts.projectDuplicated"));
    queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY, currentOrganizationId] });
  };

  const confirmDeleteProject = async () => {
    if (!deleteTargetId) return;
    setDeletingProjectId(deleteTargetId);

    const target = projects.find((p) => p.id === deleteTargetId);
    const draftBriefId =
      target?.caseBriefId ??
      (deleteTargetId.startsWith("draft-") ? Number(deleteTargetId.slice("draft-".length)) : NaN);
    const isDraftWithBrief = Number.isFinite(draftBriefId);
    const action: Promise<unknown> = isDraftWithBrief
      ? $api.delete(`/chat/create-project-chat/case-brief/${draftBriefId}`)
      : archiveProject(deleteTargetId);
    const successMessage = isDraftWithBrief ? t("toasts.draftDeleted") : t("toasts.projectDeleted");
    const failureMessage = isDraftWithBrief ? t("errors.deleteDraftFailed") : t("errors.deleteProjectFailed");

    const [, err] = await tryCatch<unknown, AxiosError<{ message: string }>>(action);
    setDeletingProjectId(null);
    if (err) {
      toast.error(err.response?.data.message || failureMessage);
      return;
    }
    toast.success(successMessage);
    queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY, currentOrganizationId] });
  };

  const onSearchChange = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value.trim() === "") next.delete("search");
        else next.set("search", value);
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (!data) return;
    setProjects(data);
  }, [data, setProjects]);

  const deleteTargetIsDraft = projects.find((p) => p.id === deleteTargetId)?.isDraft ?? false;

  const savedFilterActions = useSavedFilterActions(prefs.savedFilters);
  const saveCurrentFilters = useSaveCurrentFilters(prefs.savedFilters);
  const canSaveCurrentFilters = !isFiltersEmpty(prefs.savedFilters) && savedFilterActions.matched === null;

  const searchToolbar =
    headerSlot &&
    createPortal(
      <>
        <FilterSearch query={search} onQueryChange={onSearchChange} activeFilters={activeFilterCount}>
          <FiltersPanel
            dimensions={dimensions}
            projects={projects}
            filters={filters}
            onToggleFilterValue={toggleFilterValue}
            onClearAll={clearAllFilters}
            savedFilters={savedFilterActions.savedFilters}
            matchedSavedFilterId={savedFilterActions.matched?.id}
            onApplySavedFilter={(filter) => persist({ savedFilters: filter.filters })}
            onRenameSavedFilter={savedFilterActions.startRename}
            onDeleteSavedFilter={savedFilterActions.remove}
            onSaveCurrentFilters={canSaveCurrentFilters ? saveCurrentFilters.open : undefined}
          />
        </FilterSearch>
        {hasEdit && (
          <Button
            data-testid="projects-page-create-project"
            size="lg"
            shape="pill"
            onClick={() => navigate(getRouteCreateProject())}
          >
            <PlusIcon data-icon="inline-start" weight="bold" />
            <span className="hidden sm:inline">{t("toolbar.addProject")}</span>
          </Button>
        )}
      </>,
      headerSlot
    );

  return (
    <>
      {searchToolbar}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <LatticeField />
      </div>
      <AppShellPanels>
        <AppShellCenter>
          <AppShellMain className="mx-0 max-w-[960px] px-0 pt-4 sm:px-0">
            <AppShellContent size="wide" className="text-foreground">
              {isError && !isInvalidUrlError && !isLoading && (
                <div className="flex flex-1 items-center justify-center bg-innoveraZinc-100">
                  <div className="flex flex-col items-center gap-[22px] px-5 py-12">
                    <div className="flex flex-col items-center gap-1">
                      <FigmaTypography variant="heading-3" color="zinc900">
                        {t("errors.loadFailedTitle")}
                      </FigmaTypography>
                      <FigmaTypography variant="body-2" color="zinc500" className="w-[244px] text-center">
                        {t("errors.loadFailedBody")}
                      </FigmaTypography>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="projects-error-retry"
                      onClick={() => void refetch()}
                    >
                      <RotateCw className="h-4 w-4" />
                      {t("errors.retry")}
                    </Button>
                  </div>
                </div>
              )}

              {isInvalidUrlError && !isLoading && (
                <PortfolioInvalidUrlState
                  isPending={invalidUrlMutation.isPending}
                  onClear={() => invalidUrlMutation.mutate({ customSnippetsUrl: null })}
                />
              )}

              {isLoading && <PageLoader />}

              {!isError && !isLoading && (
                <div className="flex flex-col">
                  {projects.length === 0 && (
                    <EmptyOrgState hasEdit={hasEdit} onAddProject={() => navigate(getRouteCreateProject())} />
                  )}
                  {projects.length > 0 && derived.length === 0 && (
                    <NoProjectsFoundState
                      onClearAll={() =>
                        setSearchParams(
                          (prev) => {
                            const next = new URLSearchParams(prev);
                            next.delete("search");
                            for (const k of [...next.keys()]) {
                              if (filterKeyFromUrl(k) !== null) next.delete(k);
                            }
                            return next;
                          },
                          { replace: true }
                        )
                      }
                    />
                  )}
                  {derived.length > 0 && (
                    <div className="flex flex-col">
                      <ProjectsCards
                        projects={derived}
                        dimensions={dimensions}
                        hiddenDimensionIds={hiddenSet}
                        hiddenSystemColumns={hiddenSysSet}
                        hasEdit={hasEdit}
                        canDuplicateProject={canDuplicateProject}
                        organizationId={currentOrganizationId}
                        onListInvalidated={invalidateProjectsList}
                        onDuplicateProject={setDuplicateSourceId}
                        onDeleteProject={setDeleteTargetId}
                        deletingProjectId={deletingProjectId}
                      />
                      <div className="mt-auto pt-5">
                        <CommitHash appearance="design-system" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </AppShellContent>
          </AppShellMain>
        </AppShellCenter>
        <AppShellAside>
          {isChatDocked && (
            <div
              className={cn(
                "sticky top-(--app-header-height) flex h-[calc(100svh-var(--app-header-height))] shrink-0 py-4",
                chatDockHidden && "hidden"
              )}
            >
              <ChatPanel filterScope={chatFilterScope} isOpen={!chatDockHidden} onClose={() => toggleChat(false)} />
            </div>
          )}
        </AppShellAside>
      </AppShellPanels>

      {!isChatDocked && (
        <div
          className="fixed inset-x-0 bottom-0 top-(--app-header-height) z-30 pointer-events-none [&>*]:pointer-events-auto"
          style={headerHeight ? { top: headerHeight } : undefined}
        >
          {chatOpen && (
            <button
              type="button"
              aria-label={t("chat.closeAriaLabel")}
              data-testid="projects-chat-overlay-backdrop"
              onClick={() => toggleChat(false)}
              className="absolute inset-0 z-20 cursor-default bg-transparent"
            />
          )}
          <ChatPanel isOverlay isOpen={chatOpen} onClose={() => toggleChat(false)} filterScope={chatFilterScope} />
        </div>
      )}

      {(isChatDocked ? chatDockHidden : !chatOpen) && (
        <LeoLauncher hasUnread={hasUnreadChat} onClick={() => toggleChat(true)} />
      )}

      <ConfirmDialog
        isOpen={deleteTargetId !== null}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={confirmDeleteProject}
        title={deleteTargetIsDraft ? t("deleteModal.draftTitle") : t("deleteModal.projectTitle")}
        description={deleteTargetIsDraft ? t("deleteModal.draftDescription") : t("deleteModal.projectDescription")}
        confirmText={t("deleteModal.deleteAction")}
        cancelText={t("common:cancel")}
      />

      {savedFilterActions.renameModal}
      {saveCurrentFilters.saveModal}

      <DuplicateProjectModal
        isOpen={duplicateSourceId !== null}
        onClose={() => setDuplicateSourceId(null)}
        onConfirm={handleDuplicateConfirm}
        currentOrganizationId={currentOrganizationId}
      />
    </>
  );
}
